import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Shell } from '../../components/layout/Shell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { SunHorizon } from '@phosphor-icons/react';

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.role === 'employer') nav('/employer');
      else if (u.role === 'admin') nav('/admin');
      else nav('/candidate');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="label-eyebrow text-primary mb-4">SIGN IN</div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-6">
            Welcome back.
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md">
            Continue building the only thing that matters: verifiable capability.
          </p>
          <div className="mt-8 hidden lg:flex items-center gap-3 border border-border p-6">
            <SunHorizon size={36} weight="duotone" className="text-primary" />
            <div>
              <div className="font-display font-black text-lg">HIKARI</div>
              <div className="text-xs text-muted-foreground">Trust-based hiring</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-border bg-white p-10 space-y-6" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="label-eyebrow text-muted-foreground">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 rounded-none h-11"
              required
              data-testid="login-email"
            />
          </div>
          <div>
            <Label htmlFor="password" className="label-eyebrow text-muted-foreground">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 rounded-none h-11"
              required
              data-testid="login-password"
            />
          </div>
          <Button type="submit" disabled={loading} className="rounded-none w-full h-11" data-testid="login-submit">
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>

          {/* Demo accounts — one-click sign-in */}
          <div className="border-t border-border pt-4">
            <div className="label-eyebrow text-muted-foreground mb-2 text-center">DEMO ACCOUNTS · ONE-CLICK</div>
            <div className="grid grid-cols-1 gap-1.5 text-xs">
              {[
                { label: 'Owner · Admin (you)', email: 'Admin@hikarihiring.com', password: 'HikariEngine2026@Hiring' },
                { label: 'Candidate · Sarah Chen', email: 'sarah.chen@hikari-demo.com', password: 'Demo123!' },
                { label: 'Candidate · Jordan (inconsistent)', email: 'jordan.reese@hikari-demo.com', password: 'Demo123!' },
                { label: 'Employer · NorthStar Logistics', email: 'recruiter@northstar-demo.com', password: 'Demo123!' },
                { label: 'Admin · demo', email: 'admin@hikari.dev', password: 'Admin123!' },
              ].map((d) => (
                <button
                  type="button"
                  key={d.email}
                  onClick={() => {
                    setEmail(d.email);
                    setPassword(d.password);
                  }}
                  className="flex items-center justify-between text-left border border-border px-3 py-2 hover:bg-secondary/50 transition-colors"
                  data-testid={`demo-fill-${d.email}`}
                >
                  <span className="font-medium">{d.label}</span>
                  <span className="mono text-muted-foreground text-[10px]">FILL</span>
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-muted-foreground text-center">
            No account? <Link to="/register" className="text-primary font-medium" data-testid="login-to-register">Join Hikari</Link>
          </div>
        </form>
      </div>
    </Shell>
  );
};

export default Login;
